import { html, CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import componentStyles from './tufts-leaflet-map.css.lit';
import * as L from 'leaflet';
import 'leaflet.markercluster';
export interface Marker {
  lat: number;
  lng: number;
  title: string;
}

/**
 * The Tufts Leaflet Map component
 * Simple layout and color aware component for extending
 * @element tufts-leaflet-map
 *
 */
@customElement('tufts-leaflet-map')
export class TuftsLeafletMap extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, reflect: true, attribute: 'markers' })
  markers = '[]';

  private map?: L.Map;

  updated() {
    setTimeout(() => {
      this.map?.remove();
      this.map = L.map(this.shadowRoot?.getElementById('map') as HTMLElement, {
        dragging: !L.Browser.mobile,
      }).setView([0, 0], 0);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(this.map);

      this.addMarkers();
    }, 0);
  }

  popupMarkup(title: string) {
    return `<h2>${title}</h2>`;
  }

  private addMarkers() {
    if (!this.map) return;

    this.map.eachLayer(layer => {
      if (layer instanceof L.Marker) {
        this.map?.removeLayer(layer);
      }
    });

    const customIcon = L.icon({
      iconUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      iconRetinaUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      shadowUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41],
    });

    const markersArray = JSON.parse(this.markers) as Marker[];

    const markerClusterGroup = L.markerClusterGroup();
    markersArray.forEach(marker => {
      const newMarker = L.marker([marker.lat, marker.lng], {
        icon: customIcon,
      });

      newMarker.bindPopup(this.popupMarkup(marker.title));
      markerClusterGroup.addLayer(newMarker);
    });

    this.map.addLayer(markerClusterGroup);

    const bounds = L.latLngBounds(
      markersArray.map(marker => [marker.lat, marker.lng])
    );
    const center = bounds.getCenter();
    const zoom = this.map.getZoom();

    this.map.setView(center, zoom);
    this.map.fitBounds(bounds);
  }

  render() {
    return html`<div id="map"></div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-leaflet-map': TuftsLeafletMap;
  }
}
